import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Sparkles, Calendar, MapPin, Heart, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';
import formatEventDate from '../lib/formatEventDate';
import { supportsCapability } from '../lib/capabilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function EventSitePage() {
  const { auraKey } = useParams();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // RSVP form state
  const [rsvp, setRsvp] = useState({ guest_name: '', email: '', attending: 'yes', party_size: 1, dietary_notes: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rsvpError, setRsvpError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/event-site/${auraKey}`)
      .then(({ data }) => setSite(data))
      .catch(() => setError('This event site is not yet published or does not exist.'))
      .finally(() => setLoading(false));
  }, [auraKey]);

  const handleRsvp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setRsvpError('');
    try {
      await axios.post(`${API_URL}/api/event-site/${auraKey}/rsvp`, rsvp);
      setSubmitted(true);
    } catch (err) {
      setRsvpError(err.response?.data?.detail || 'Failed to submit RSVP.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-midnight flex items-center justify-center"><Sparkles className="w-8 h-8 text-champagne animate-pulse" /></div>;
  if (error) return (
    <div className="min-h-screen bg-midnight flex items-center justify-center p-4">
      <div className="aura-card rounded-2xl p-8 text-center max-w-md">
        <p className="text-text-muted mb-4">{error}</p>
        <Link to="/" className="text-champagne hover:underline">Return to Memouraa</Link>
      </div>
    </div>
  );

  const c = site.content || {};
  const isLight = site.template === 'classic' || site.template === 'minimal';
  const fontFamily = site.template === 'classic' ? 'serif' : 'sans-serif';

  const eventLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": site.title,
    "startDate": site.event_date,
    ...(site.end_date ? { "endDate": site.end_date } : {}),
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/MixedEventAttendanceMode",
    "location": { "@type": "Place", "name": "Memouraa Live Gallery", "address": "Online" },
    "image": site.cover_image ? `${API_URL}${site.cover_image}` : `${API_URL}/api/og/aura/${auraKey}.png`,
    "description": site.description || "Capture the Aura.",
    "organizer": { "@type": "Organization", "name": "Memouraa", "url": "https://memouraa.com" }
  };

  return (
    <div className="min-h-screen bg-midnight">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(eventLd)}</script>
      </Helmet>
      <SiteHeader />
    <div
      className={`${isLight ? 'bg-stone-50 text-stone-900' : 'bg-midnight text-text-light'}`}
      style={{ backgroundColor: site.primary_color, fontFamily }}
    >
      {/* Cover */}
      <div className="relative">
        {site.cover_image && (
          <img src={site.cover_image} alt={site.title} className="w-full h-72 sm:h-96 object-cover opacity-60" />
        )}
        <div className="absolute inset-0 flex items-center justify-center text-center p-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl sm:text-6xl font-serif font-medium mb-3" style={{ color: site.accent_color }}>
              {site.title}
            </h1>
            <div className="flex items-center justify-center gap-2 text-sm opacity-80">
              <Calendar className="w-4 h-4" /> {formatEventDate(site)}
            </div>
            {c.location && (
              <div className="flex items-center justify-center gap-2 text-sm opacity-80 mt-1">
                <MapPin className="w-4 h-4" /> {c.location}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-12 space-y-12">
        {/* Welcome */}
        {(site.description || c.welcome) && (
          <section>
            <h2 className="text-2xl font-serif mb-3" style={{ color: site.accent_color }}>Welcome</h2>
            <p className="leading-relaxed whitespace-pre-line opacity-90">{c.welcome || site.description}</p>
          </section>
        )}

        {/* Schedule */}
        {Array.isArray(c.schedule) && c.schedule.length > 0 && (
          <section>
            <h2 className="text-2xl font-serif mb-4" style={{ color: site.accent_color }}>Schedule</h2>
            <ul className="space-y-3">
              {c.schedule.map((s, i) => (
                <li key={i} className="flex gap-4 border-l-2 pl-4 py-1" style={{ borderColor: site.accent_color }}>
                  <span className="font-medium min-w-[5rem]">{s.time}</span>
                  <span className="opacity-90">{s.event}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Dress code */}
        {c.dress_code && (
          <section>
            <h2 className="text-2xl font-serif mb-3" style={{ color: site.accent_color }}>Dress Code</h2>
            <p className="opacity-90">{c.dress_code}</p>
          </section>
        )}

        {/* Memouraa portal CTA */}
        <section className="text-center py-8 rounded-2xl" style={{ backgroundColor: `${site.accent_color}15`, border: `1px solid ${site.accent_color}40` }}>
          <Camera className="w-10 h-10 mx-auto mb-3" style={{ color: site.accent_color }} />
          <p className="mb-4 text-lg">Share your favorite moments during the event</p>
          <a href={site.portal_url} className="inline-block px-6 py-3 rounded-full font-medium" style={{ backgroundColor: site.accent_color, color: site.primary_color }}>
            Open Photo Portal
          </a>
        </section>

        {/* RSVP — only for occasions that support guest RSVPs */}
        {supportsCapability(site, 'rsvp') && (
        <section>
          <h2 className="text-2xl font-serif mb-4" style={{ color: site.accent_color }}>RSVP</h2>
          {submitted ? (
            <div className="text-center py-8">
              <Heart className="w-12 h-12 mx-auto mb-3" style={{ color: site.accent_color }} />
              <p className="text-xl">Thank you!</p>
              <p className="opacity-80 mt-1">Your response has been recorded.</p>
            </div>
          ) : (
            <form onSubmit={handleRsvp} className="space-y-4">
              <input
                type="text"
                required
                placeholder="Your full name"
                value={rsvp.guest_name}
                onChange={e => setRsvp({ ...rsvp, guest_name: e.target.value })}
                className={`w-full px-4 py-3 rounded-lg ${isLight ? 'bg-white border border-stone-300' : 'aura-input'}`}
              />
              <input
                type="email"
                placeholder="Email (optional)"
                value={rsvp.email}
                onChange={e => setRsvp({ ...rsvp, email: e.target.value })}
                className={`w-full px-4 py-3 rounded-lg ${isLight ? 'bg-white border border-stone-300' : 'aura-input'}`}
              />
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={rsvp.attending}
                  onChange={e => setRsvp({ ...rsvp, attending: e.target.value })}
                  className={`px-4 py-3 rounded-lg ${isLight ? 'bg-white border border-stone-300' : 'aura-input'}`}
                >
                  <option value="yes">Joyfully accept</option>
                  <option value="no">Regretfully decline</option>
                  <option value="maybe">Maybe</option>
                </select>
                <input
                  type="number"
                  min="1"
                  max="20"
                  placeholder="Party size"
                  value={rsvp.party_size}
                  onChange={e => setRsvp({ ...rsvp, party_size: parseInt(e.target.value) || 1 })}
                  className={`px-4 py-3 rounded-lg ${isLight ? 'bg-white border border-stone-300' : 'aura-input'}`}
                />
              </div>
              <textarea
                placeholder="Dietary restrictions or allergies (optional)"
                value={rsvp.dietary_notes}
                onChange={e => setRsvp({ ...rsvp, dietary_notes: e.target.value })}
                rows={2}
                className={`w-full px-4 py-3 rounded-lg resize-none ${isLight ? 'bg-white border border-stone-300' : 'aura-input'}`}
              />
              <textarea
                placeholder="A note for the host (optional)"
                value={rsvp.message}
                onChange={e => setRsvp({ ...rsvp, message: e.target.value })}
                rows={2}
                className={`w-full px-4 py-3 rounded-lg resize-none ${isLight ? 'bg-white border border-stone-300' : 'aura-input'}`}
              />
              {rsvpError && <p className="text-red-500 text-sm">{rsvpError}</p>}
              <button
                type="submit"
                disabled={submitting || !rsvp.guest_name.trim()}
                className="w-full py-3 rounded-lg font-medium"
                style={{ backgroundColor: site.accent_color, color: site.primary_color }}
              >
                {submitting ? 'Sending...' : 'Send RSVP'}
              </button>
            </form>
          )}
        </section>
        )}

        {/* Footer */}
        <footer className="text-center text-xs opacity-60 pt-8 pb-4">
          Powered by Memouraa
        </footer>
      </div>
    </div>
      <FooterExpanded />
    </div>
  );
}
